import { pick } from "lodash";
import { CHAT_BOT_LOADER_DEFAULT_SETTINGS } from "../consts";
import { ChatBotLoaderSettings } from "../types";
import { resolveChatBotSettings } from "./resolveChatBotSettings";

const LOADER_SETTINGS_KEYS = Object.keys(
  CHAT_BOT_LOADER_DEFAULT_SETTINGS
) as (keyof ChatBotLoaderSettings)[];

const formatSettingsLiteral = (settings: ChatBotLoaderSettings): string => {
  const PROP_INDENT = "      "; // 2 (script body) + 4 (constructor object key)
  const CLOSE_INDENT = "    "; // 2 (script body) + 2 (constructor object)

  const lines = Object.entries(settings).map(
    ([key, value]) => `${PROP_INDENT}${key}: ${JSON.stringify(value)}`
  );

  return `{\n${lines.join(",\n")}\n${CLOSE_INDENT}}`;
};

interface BuildChatBotInstallationSnippetsOptions {
  token: string;
  rawSettings: Record<string, unknown>;
  chatbotUrl: string;
}

/**
 * Compiles all installation code snippets for a chatbot widget.
 *
 * Only the loader-specific fields are embedded in the generated snippets.
 * All other settings are served by the widget API and do not need to be repeated in the install code.
 *
 */
export const buildChatBotInstallationSnippets = ({
  token,
  rawSettings,
  chatbotUrl,
}: BuildChatBotInstallationSnippetsOptions) => {
  const loaderSettings = pick(
    resolveChatBotSettings(rawSettings),
    LOADER_SETTINGS_KEYS
  );

  const webScript = [
    `<script src="${chatbotUrl}/bot.min.js"></script>`,
    `<script>`,
    `  const chatbot = new window.jugl.ChatBot({`,
    `    token: "${token}",`,
    `    settings: ${formatSettingsLiteral(loaderSettings)},`,
    `    chatbotUrl: "${chatbotUrl}"`,
    `  });`,
    `</script>`,
  ].join("\n");

  const mobileUrlParams = new URLSearchParams({
    token,
    mobile: "true",
  });

  const mobileUrl = `${chatbotUrl}/?${mobileUrlParams.toString()}`;

  const mobileCode = [
    `// Load this URL in your mobile WebView`,
    `const chatbotUrl = "${mobileUrl}";`,
    `webView.loadUrl(chatbotUrl);`,
  ].join("\n");

  return { webScript, mobileUrl, mobileCode };
};
